import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ArrowLeft, User } from 'lucide-react';
import { useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL;

const callStatusOptions = [
  { value: 'not_interested', label: 'Not Interested' },
  { value: 'warm_lead',      label: 'Interested' },
  { value: 'call_back',      label: 'Call Back' },
  { value: 'cold_lead',      label: 'Follow Up' },
  { value: 'not_connected',  label: 'Not Connected' },
];

const callStatusLabel = val =>
  callStatusOptions.find(o => o.value === val)?.label || val || '—';

const statusBadgeClass = val => {
  switch (val) {
    case 'warm_lead':      return 'bg-orange-50 text-orange-700 border-orange-200';
    case 'call_back':      return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'not_interested': return 'bg-red-50 text-red-600 border-red-200';
    case 'cold_lead':      return 'bg-gray-100 text-gray-600 border-gray-300';
    case 'not_connected':  return 'bg-yellow-50 text-yellow-700 border-yellow-200';
    default:                return 'bg-gray-50 text-gray-500 border-gray-200';
  }
};

const formatDateTime = d =>
  d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

const formatDate = d =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const StatusBadge = ({ value }) =>
  value ? (
    <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full border ${statusBadgeClass(value)}`}>
      {callStatusLabel(value)}
    </span>
  ) : <span className="text-gray-400 text-xs">—</span>;

const tableHeaders = ['When', 'Status', 'Loan Type', 'Amount', 'Referred By', 'Call Back Date', 'Disposition', 'Remarks', 'Changed By', 'Docs'];

const docsSummary = docs => {
  if (!docs || docs.length === 0) return '—';
  const uploaded = docs.filter(d => d.fileUrl).length;
  return `${uploaded}/${docs.length}`;
};

// One row shared by both the current row and each history row
function DetailRow({ entry, when, badge, highlight }) {
  return (
    <tr className={highlight ? 'bg-blue-50/40 hover:bg-blue-50/60' : 'hover:bg-gray-50'}>
      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
        <div className="flex items-center gap-2">
          {when}
          {badge}
        </div>
      </td>
      <td className="px-4 py-3 text-sm"><StatusBadge value={entry.callStatus} /></td>
      <td className="px-4 py-3 text-sm text-gray-600">{entry.loanType || '—'}</td>
      <td className="px-4 py-3 text-sm text-gray-600">{entry.amountRequired != null ? `₹${Number(entry.amountRequired).toLocaleString('en-IN')}` : '—'}</td>
      <td className="px-4 py-3 text-sm text-gray-600">{entry.referredBy || '—'}</td>
      <td className="px-4 py-3 text-sm text-gray-500">{entry.callBackDate ? formatDate(entry.callBackDate) : '—'}</td>
      <td className="px-4 py-3 text-sm text-gray-600">{entry.dispositionChecked ? (entry.dispositionRemark || 'Yes') : '—'}</td>
      <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate" title={entry.remarks || ''}>{entry.remarks || '—'}</td>
      <td className="px-4 py-3 text-sm text-gray-500">{entry.changedByName || entry.changedBy?.name || '—'}</td>
      <td className="px-4 py-3 text-sm text-gray-500">{docsSummary(entry.reqDocuments)}</td>
    </tr>
  );
}

// ── Unified Case Name / Mobile / Case Timeline table ────────────────────────────
function CaseTable({ record, history }) {
  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[960px]">
          <thead>
            <tr>
              {tableHeaders.map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            <DetailRow
              entry={record}
              when={formatDateTime(record.updatedAt)}
              highlight
              badge={<span className="text-[10px] font-semibold bg-green-100 text-green-700 px-1.5 py-0.5 rounded uppercase tracking-wide">Current</span>}
            />
            {history.map((h, i) => (
              <DetailRow key={i} entry={h} when={formatDateTime(h.recordedAt)} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function CallerDetail() {
  const { id } = useParams();
  const { userInfo } = useSelector(s => s.userLogin || {});
  const authConfig = { headers: userInfo?.token ? { Authorization: `Bearer ${userInfo.token}` } : {} };

  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    setError('');
    axios.get(`${API}/callers/${id}`, authConfig)
      .then(({ data }) => setRecord(data))
      .catch(err => setError(err?.response?.data?.message || 'Failed to load caller details.'))
      .finally(() => setLoading(false));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const history = [...(record?.history || [])].sort(
    (a, b) => new Date(b.recordedAt) - new Date(a.recordedAt)
  );

  return (
    <div className="px-2 py-3 sm:p-6 bg-gray-50 min-h-screen">
      <Link
        to="/admin/caller-list"
        className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 mb-4 transition-colors"
      >
        <ArrowLeft size={15} />
        Back to Caller List
      </Link>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : error ? (
        <p className="text-center text-red-500 text-sm py-12">{error}</p>
      ) : !record ? (
        <p className="text-center text-gray-400 text-sm py-12">Not found.</p>
      ) : (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h1 className="text-xl sm:text-2xl font-semibold text-gray-800">{record.caseName || '—'}</h1>
              <p className="text-xs text-gray-500 mt-0.5">{record.mobileNumber || '—'}</p>
            </div>
            <span className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-700 text-xs font-medium px-2.5 py-1 rounded-full border border-blue-100">
              <User size={11} />
              {record.caller?.name || record.callerName || '—'}
            </span>
          </div>

          <div>
            <h2 className="text-sm font-semibold text-gray-700 mb-3">
              Case Timeline {history.length > 0 ? `(${history.length + 1} entries)` : ''}
            </h2>
            <CaseTable record={record} history={history} />
          </div>

          {(record.reqDocuments || []).length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-gray-700 mb-3">Current Documents</h2>
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 space-y-2">
                {record.reqDocuments.map((d, i) => (
                  <div key={i} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                    <span className="text-sm text-gray-700">{d.name}</span>
                    {d.fileUrl
                      ? <a href={d.fileUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:text-blue-800 font-medium underline underline-offset-2">View</a>
                      : <span className="text-xs text-gray-400">Not uploaded</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
